import { readTheMapLanguage } from '../domains/app/the-map-speaks-the-language-of-the-app';
import { ensureGoogleMapsLoaded } from './google-maps-loader.util';
import { getSharedGoogleGeocoder } from './place-geocode-cache.util';

/**
 * Turning a position into a place, asked of Google once per spot and per phone.
 *
 * WHY THIS EXISTS. Google Cloud billed 257 € of Geocoding for the 30 days to
 * 2 November 2026: 62 315 calls, up 376 % on the month before. The request
 * counts by key put all of it on the app's JavaScript geocoder (52 579 on the
 * mobile key, 18 263 on the old one that releases before #552 still carry),
 * and nothing on the back office. Every one of those calls was a
 * `new google.maps.Geocoder().geocode({ location })` with no memory at all:
 * Explore asked again on every visit to the tab, for a member who had not
 * moved, and so did the location sync, the search screens and the invite
 * sheet.
 *
 * WHAT CHANGES. Positions are rounded to three decimals, about a hundred
 * metres, and the answer for that square is kept in localStorage, so it
 * survives the app being closed. A member standing still costs nothing; a new
 * call means the member really moved, or switched the app's language. Two
 * callers asking at once share one request. Failures are not kept, and never
 * thrown: the caller gets an empty list, which every one of them already
 * handled as « Google knows nothing here ».
 */
export const REVERSE_GEOCODE_STORAGE_KEY = 'smatchy.reverseGeocode.v1';

/** Three decimals of a degree is 111 m of latitude: the same street, the same town. */
const PRECISION = 3;
/** A town does not move. Twenty squares of a dozen results stay well under the storage quota. */
const CACHE_TTL_MS = 90 * 24 * 60 * 60 * 1000;
const CACHE_LIMIT = 20;

/** What is kept of a result: the parts the app reads, and nothing that is a function. */
interface StoredGeocoderResult {
  address_components: google.maps.GeocoderAddressComponent[];
  formatted_address: string;
  place_id: string;
  types: string[];
  location?: { lat: number; lng: number };
}

interface StoredEntry {
  savedAt: number;
  results: StoredGeocoderResult[];
}

type StoredCache = Record<string, StoredEntry>;

const inFlight = new Map<string, Promise<google.maps.GeocoderResult[]>>();

export const reverseGeocodeSquare = (lat: number, lng: number, language: string): string =>
  `${lat.toFixed(PRECISION)},${lng.toFixed(PRECISION)}:${language}`;

const readStore = (): StoredCache => {
  try {
    const raw = localStorage.getItem(REVERSE_GEOCODE_STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : {};
    return parsed && typeof parsed === 'object' ? (parsed as StoredCache) : {};
  } catch {
    return {};
  }
};

const writeStore = (store: StoredCache): void => {
  const newestFirst = Object.entries(store)
    .sort(([, left], [, right]) => right.savedAt - left.savedAt)
    .slice(0, CACHE_LIMIT);
  try {
    localStorage.setItem(REVERSE_GEOCODE_STORAGE_KEY, JSON.stringify(Object.fromEntries(newestFirst)));
  } catch {
    // A full or refused storage only costs the next call.
  }
};

const toStored = (result: google.maps.GeocoderResult): StoredGeocoderResult => {
  const location = result.geometry?.location;
  return {
    address_components: result.address_components ?? [],
    formatted_address: result.formatted_address ?? '',
    place_id: result.place_id ?? '',
    types: result.types ?? [],
    location: location ? { lat: location.lat(), lng: location.lng() } : undefined,
  };
};

/** Back to the shape the SDK hands over, as far as the app reads it. */
const fromStored = (stored: StoredGeocoderResult): google.maps.GeocoderResult => {
  const { location, ...rest } = stored;
  return {
    ...rest,
    geometry: location ? { location: { lat: () => location.lat, lng: () => location.lng } } : undefined,
  } as unknown as google.maps.GeocoderResult;
};

const askGoogle = async (lat: number, lng: number): Promise<google.maps.GeocoderResult[]> => {
  if (!(await ensureGoogleMapsLoaded())) {
    return [];
  }
  const geocoder = getSharedGoogleGeocoder();
  if (!geocoder) {
    return [];
  }
  try {
    const response = await geocoder.geocode({ location: { lat, lng } });
    return response?.results ?? [];
  } catch {
    // OVER_QUERY_LIMIT and friends arrive as a rejection (CAPACITOR-12).
    return [];
  }
};

/**
 * The geocoder's answer for a position, from this phone's memory when the
 * square has been asked before. Never throws; an empty list means no answer.
 */
export function getCachedReverseGeocode(position: { lat: number; lng: number }): Promise<google.maps.GeocoderResult[]> {
  const key = reverseGeocodeSquare(position.lat, position.lng, readTheMapLanguage());
  const stored = readStore()[key];
  if (stored && Date.now() - stored.savedAt < CACHE_TTL_MS && stored.results.length) {
    return Promise.resolve(stored.results.map(fromStored));
  }

  const pending = inFlight.get(key);
  if (pending) {
    return pending;
  }

  const request = askGoogle(position.lat, position.lng)
    .then((results) => {
      if (results.length) {
        writeStore({ ...readStore(), [key]: { savedAt: Date.now(), results: results.map(toStored) } });
      }
      return results;
    })
    .finally(() => inFlight.delete(key));
  inFlight.set(key, request);
  return request;
}
